// Type for translation object - using Record to avoid circular dependencies
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TranslationObject = any

/**
 * Translates a day name string (e.g., "Monday") to the localized version
 * @param dayName - The English day name (case-insensitive)
 * @param t - Translation object
 * @returns Translated day name
 */
export const translateDayName = (dayName: string, t: TranslationObject): string => {
  const dayMap: Record<string, string> = {
    'monday': t.common.day.monday,
    'tuesday': t.common.day.tuesday,
    'wednesday': t.common.day.wednesday,
    'thursday': t.common.day.thursday,
    'friday': t.common.day.friday,
    'saturday': t.common.day.saturday,
    'sunday': t.common.day.sunday,
  }
  return dayMap[dayName.toLowerCase()] || dayName
}

/**
 * Gets the short (3-char) translated day name from a Date object
 * @param date - Date object
 * @param t - Translation object
 * @returns Short day name (e.g., "Пн", "Mon")
 */
export const getShortDayName = (date: Date, t: TranslationObject): string => {
  const dayIndex = date.getDay() // 0=Sunday, 1=Monday, ...
  const days = [
    t.common.dayAbbr.sun,
    t.common.dayAbbr.mon,
    t.common.dayAbbr.tue,
    t.common.dayAbbr.wed,
    t.common.dayAbbr.thu,
    t.common.dayAbbr.fri,
    t.common.dayAbbr.sat,
  ]
  return days[dayIndex]
}

/**
 * Gets the short (3-char) month abbreviation using translations
 * @param date - Date object
 * @param t - Translation object
 * @returns Short month name (e.g., "Січ", "Jan")
 */
export const getShortMonthName = (date: Date, t: TranslationObject): string => {
  const monthIndex = date.getMonth()
  const months = [
    t.common.month.january,
    t.common.month.february,
    t.common.month.march,
    t.common.month.april,
    t.common.month.may,
    t.common.month.june,
    t.common.month.july,
    t.common.month.august,
    t.common.month.september,
    t.common.month.october,
    t.common.month.november,
    t.common.month.december,
  ]
  return months[monthIndex]
}

/**
 * Formats a date in localized short format: "Day, Mon DD"
 * Example: "Пон, Січ 26" or "Mon, Jan 26"
 * @param date - Date object
 * @param t - Translation object
 * @returns Formatted date string
 */
export const formatShortDate = (date: Date, t: TranslationObject): string => {
  const dayName = getShortDayName(date, t)
  const monthName = getShortMonthName(date, t)
  const day = date.getDate()
  return `${dayName}, ${monthName} ${day}`
}

/**
 * Format date range for display (e.g., "Січ 15 - Січ 21" or "Січ 15 - 21")
 * @param startDate - Start date
 * @param endDate - End date
 * @param t - Translation object
 * @returns Formatted date range string
 */
export const formatDateRange = (startDate: Date, endDate: Date, t: TranslationObject): string => {
  const startMonth = getShortMonthName(startDate, t)
  const startDay = startDate.getDate()
  const endMonth = getShortMonthName(endDate, t)
  const endDay = endDate.getDate()
  
  // If same month, show month only once: "Січ 15 - 21"
  if (startMonth === endMonth) {
    return `${startMonth} ${startDay} - ${endDay}`
  }
  
  // Different months: "Січ 15 - Лют 21"
  return `${startMonth} ${startDay} - ${endMonth} ${endDay}`
}

/**
 * Calendar date from a backend LocalDateTime / ISO string, ignoring clock time and timezone.
 * Avoids off-by-one when a naive `2026-08-13T15:14:25` is parsed as local or UTC.
 */
export function formatCalendarDate(value: string, language: string): string {
  const match = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})/)
  const date = match
    ? new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])))
    : new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleDateString(language, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  })
}

/**
 * Format full date with weekday (e.g., "Понеділок, Січ 26")
 * @param date - Date object
 * @param t - Translation object
 * @returns Formatted date string with full weekday name
 */
export const formatLongDate = (date: Date, t: TranslationObject): string => {
  const dayIndex = date.getDay()
  const days = [
    t.common.day.sunday,
    t.common.day.monday,
    t.common.day.tuesday,
    t.common.day.wednesday,
    t.common.day.thursday,
    t.common.day.friday,
    t.common.day.saturday,
  ]
  const dayName = days[dayIndex]
  const monthName = getShortMonthName(date, t)
  const day = date.getDate()
  
  return `${dayName}, ${monthName} ${day}`
}

/**
 * Get short day abbreviation from day name string (e.g., "MONDAY" -> "Пн")
 * @param dayName - Day name in uppercase (e.g., "MONDAY", "TUESDAY")
 * @param t - Translation object
 * @returns Short day abbreviation
 */
export const getDayAbbreviation = (dayName: string, t: TranslationObject): string => {
  const dayMap: Record<string, string> = {
    'MONDAY': t.common.dayAbbr.mon,
    'TUESDAY': t.common.dayAbbr.tue,
    'WEDNESDAY': t.common.dayAbbr.wed,
    'THURSDAY': t.common.dayAbbr.thu,
    'FRIDAY': t.common.dayAbbr.fri,
    'SATURDAY': t.common.dayAbbr.sat,
    'SUNDAY': t.common.dayAbbr.sun,
  }
  return dayMap[dayName] || dayName.slice(0, 3)
}

/**
 * Format duration with translated hour/minute abbreviations
 * @param duration - Duration object with hours and minutes
 * @param t - Translation object
 * @returns Formatted duration string (e.g., "1h 30m" or "1г 30хв")
 */
export const formatDurationTranslated = (duration: { hours: number; minutes: number }, t: TranslationObject): string => {
  const { hours, minutes } = duration
  
  if (hours > 0 && minutes > 0) {
    return `${hours}${t.createTrainingSet.templateCard.hours} ${minutes}${t.createTrainingSet.templateCard.minutes}`
  } else if (hours > 0) {
    return `${hours}${t.createTrainingSet.templateCard.hours}`
  } else if (minutes > 0) {
    return `${minutes}${t.createTrainingSet.templateCard.minutes}`
  }
  return '0m'
}

/**
 * Format a date in a user-friendly relative format
 * Examples: "today", "tomorrow", "on Friday", "in 2 days", "in 2 weeks"
 * @param date - Date object or ISO string
 * @param t - Translation object
 * @returns Formatted relative date string
 */
export const formatRelativeDate = (date: Date | string, t: TranslationObject): string => {
  const targetDate = typeof date === 'string' ? new Date(date) : date
  const now = new Date()
  
  // Reset time to start of day for comparison
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const target = new Date(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate())
  
  // Calculate difference in days
  const diffTime = target.getTime() - today.getTime()
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
  
  // Today
  if (diffDays === 0) {
    return t.common.relativeDates.today
  }
  
  // Tomorrow
  if (diffDays === 1) {
    return t.common.relativeDates.tomorrow
  }
  
  // Within next week (2-7 days) - show day name
  if (diffDays >= 2 && diffDays <= 7) {
    const dayIndex = targetDate.getDay()
    const days = [
      t.common.day.sunday,
      t.common.day.monday,
      t.common.day.tuesday,
      t.common.day.wednesday,
      t.common.day.thursday,
      t.common.day.friday,
      t.common.day.saturday,
    ]
    const dayName = days[dayIndex]
    return t.common.relativeDates.onDay.replace('{{day}}', dayName)
  }
  
  // Within 2 weeks (8-14 days) - show "in X days"
  if (diffDays >= 8 && diffDays <= 14) {
    const baseText = t.common.relativeDates.inDays.replace('{{count}}', diffDays.toString())
    // Handle Ukrainian pluralization for days (word is separate)
    if (t.common.relativeDates.days1 && !baseText.includes('days') && !baseText.includes('day')) {
      let dayWord = t.common.relativeDates.days5plus
      if (diffDays === 1) {
        dayWord = t.common.relativeDates.days1
      } else if (diffDays >= 2 && diffDays <= 4) {
        dayWord = t.common.relativeDates.days2_4
      }
      return `${baseText} ${dayWord}`
    }
    return baseText
  }
  
  // More than 2 weeks - show "in X weeks"
  const weeks = Math.ceil(diffDays / 7)
  const baseText = t.common.relativeDates.inWeeks.replace('{{count}}', weeks.toString())
  // Handle Ukrainian pluralization for weeks (word is separate)
  if (t.common.relativeDates.weeks1 && !baseText.includes('weeks') && !baseText.includes('week')) {
    let weekWord = t.common.relativeDates.weeks5plus
    if (weeks === 1) {
      weekWord = t.common.relativeDates.weeks1
    } else if (weeks >= 2 && weeks <= 4) {
      weekWord = t.common.relativeDates.weeks2_4
    }
    return `${baseText} ${weekWord}`
  }
  return baseText
}
